import React, { useCallback, useContext } from "react";
import { forEach, has, set, isObject } from "lodash-es";
import { useImmerReducer } from "use-immer";
import { produce } from "immer";

const PermissionContext = React.createContext({});

export const usePermissions = () => useContext(PermissionContext);

const INSERT_GLOBAL_PERMISSION = "INSERT_GLOBAL_PERMISSION";
const INSERT_RESOURCE_PERMISSION = "INSERT_RESOURCE_PERMISSION";
const INSERT_INSTANCE_PERMISSION = "INSERT_INSTANCE_PERMISSION";

const defaultResourceObj = (resourceName) => ({
  resource: resourceName,
  abilities: {},
  instances: {},
});

const defaultInstanceAbility = (instanceId) => ({
  id: instanceId,
  abilities: {},
});

const insertIfNotExists = (obj, path, value) => {
  const exists = has(obj, path);
  if (!exists) {
    set(obj, path, value);
  }
};

const permissionReducer = (draft, action) => {
  const { resourceName, instanceId, ability } = action.payload;

  if (resourceName) {
    insertIfNotExists(draft.resourcePermissions, resourceName, defaultResourceObj(resourceName));
  }

  switch (action.type) {
    case INSERT_GLOBAL_PERMISSION:
      set(draft.globalPermissions, ability.ability, ability);
      break;
    case INSERT_RESOURCE_PERMISSION:
      set(draft.resourcePermissions, `${resourceName}.abilities.${ability.ability}`, ability);
      break;
    case INSERT_INSTANCE_PERMISSION: {
      const instancePath = `${resourceName}.instances.${instanceId}`;
      insertIfNotExists(draft.resourcePermissions, instancePath, defaultInstanceAbility(instanceId));

      set(draft.resourcePermissions, `${instancePath}.abilities.${ability.ability}`, ability);
      break;
    }
  }
};

const globalPermissionAction = ({ permissionName, permitted }) => {
  const ability = { ability: permissionName, permitted: Boolean(permitted) };
  return { type: INSERT_GLOBAL_PERMISSION, payload: { ability } };
};

const resourcePermissionAction = ({ resourceName, permissionName, permitted }) => {
  const ability = {
    ability: permissionName,
    permitted: permitted === CHECK_PERMISSION ? CHECK_PERMISSION : Boolean(permitted),
  };
  return { type: INSERT_RESOURCE_PERMISSION, payload: { resourceName, ability } };
};

const instancePermissionAction = ({ resourceName, instanceId, permissionName, permitted }) => {
  const ability = { ability: permissionName, permitted: Boolean(permitted) };
  return { type: INSERT_INSTANCE_PERMISSION, payload: { resourceName, instanceId, ability } };
};

const permissionAction = ({ permissionName, resourceName = null, instanceId = null, permitted = false }) => {
  if (resourceName === null && instanceId === null) {
    return globalPermissionAction({ permissionName, permitted });
  } else if (instanceId === null) {
    return resourcePermissionAction({ permissionName, resourceName, permitted });
  } else {
    return instancePermissionAction({ permissionName, resourceName, instanceId, permitted });
  }
};

const CHECK_PERMISSION = "check";

const getInitialPermissions = () => {
  let permissions = {
    globalPermissions: {},
    resourcePermissions: {},
  };

  if (window.userPermissions) {
    forEach(window.userPermissions, (perm, key) => {
      if (perm === true || perm === false || perm === CHECK_PERMISSION) {
        const action = globalPermissionAction({ permissionName: key, permitted: perm });
        permissions = produce(permissions, (draft) => permissionReducer(draft, action));
      } else if (isObject(perm)) {
        forEach(perm, (permitted, permissionName) => {
          const action = resourcePermissionAction({ resourceName: key, permissionName, permitted });
          permissions = produce(permissions, (draft) => permissionReducer(draft, action));
        });
      }
    });
  }

  return permissions;
};

const INITIAL_PERMISSION_STATE = getInitialPermissions();

export const PermissionContextProvider = ({ children }) => {
  const [permissionState, dispatch] = useImmerReducer(permissionReducer, INITIAL_PERMISSION_STATE);

  const insertGlobalPermission = useCallback((args) => dispatch(globalPermissionAction(args)), [dispatch]);
  const insertResourcePermission = useCallback((args) => dispatch(resourcePermissionAction(args)), [dispatch]);
  const insertInstancePermission = useCallback((args) => dispatch(instancePermissionAction(args)), [dispatch]);
  const insertPermission = useCallback((args) => dispatch(permissionAction(args)), [dispatch]);

  const checkPermission = (permissionName, resourceName = null, instanceId = null) => {
    if (resourceName === null && instanceId === null) {
      return permissionState.globalPermissions[permissionName]?.permitted ?? false;
    }

    const canOnInstance =
      permissionState.resourcePermissions[resourceName]?.instances?.[instanceId]?.abilities?.[permissionName]?.permitted;
    const canOnResource = permissionState.resourcePermissions[resourceName]?.abilities?.[permissionName]?.permitted;

    return canOnInstance ?? canOnResource ?? false;
  };

  const context = {
    insertPermission,
    checkPermission,
    insertGlobalPermission,
    insertResourcePermission,
    insertInstancePermission,
  };

  return <PermissionContext.Provider value={context}>{children}</PermissionContext.Provider>;
};
